import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Trophy, Medal, Target, RotateCcw } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import BottomTabBar from '../components/BottomTabBar';
import { apiMethods } from '../services/api';
import { getInitials, formatDisplayName } from '../utils/textUtils';

const LeaderboardPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  
  // Refs for infinite scroll and abort control
  const sentinelRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const observerRef = useRef(null);

  const LIMIT = 50; // Default limit as specified
  const MAX_RETRIES = 3;

  // Cleanup function for AbortController
  const cleanupAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const loadLeaderboard = useCallback(async (reset = false, query = '') => {
    // Prevent multiple simultaneous requests
    if (isLoadingRef.current && !reset) return;
    
    // Don't make request if we already know there's no more data
    if (!reset && !hasMore) return;
    
    // Cancel any existing request
    cleanupAbortController();
    
    try {
      const currentOffset = reset ? 0 : totalLoaded;
      
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      
      if (reset) {
        setLoading(true);
        setError(null);
        setRetryCount(0);
        setTotalLoaded(0);
        setHasMore(true); // Reset hasMore on fresh load
      } else {
        setLoadingMore(true);
      }
      
      isLoadingRef.current = true;

      const params = {
        limit: LIMIT,
        offset: currentOffset,
      };

      if (query.trim()) {
        params.search = query.trim();
      }

      const response = await apiMethods.leaderboard({
        ...params,
        signal: abortControllerRef.current.signal // Add abort signal
      });
      
      const newPlayers = response.data.items || response.data || [];
      const responseTotal = response.data.total ?? (newPlayers.length < LIMIT ? currentOffset + newPlayers.length : currentOffset + newPlayers.length + 1);

      if (reset) {
        setPlayers(newPlayers);
        setTotalLoaded(newPlayers.length);
      } else {
        // Avoid duplicates by checking if we already have these players
        setPlayers(prev => {
          const existingIds = new Set(prev.map(p => p.id || p.user_id));
          const uniqueNewPlayers = newPlayers.filter(p => !existingIds.has(p.id || p.user_id));
          return [...prev, ...uniqueNewPlayers];
        });
        setTotalLoaded(prev => prev + newPlayers.length);
      }

      setTotal(responseTotal);
      
      // More robust hasMore logic
      const newTotalLoaded = reset ? newPlayers.length : totalLoaded + newPlayers.length;
      const shouldHaveMore = newPlayers.length === LIMIT && newTotalLoaded < responseTotal;
      setHasMore(shouldHaveMore);
      
      setRetryCount(0); // Reset retry count on success

    } catch (err) {
      // Don't set error if request was aborted (component unmounting or intentional cancellation)
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('Request was aborted, ignoring error');
        return;
      }
      
      console.error('Error loading leaderboard:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
      
      // Increment retry count for transient errors
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
      // Don't clear abortControllerRef here to avoid race conditions
    }
  }, [totalLoaded, hasMore, cleanupAbortController]);

  // Auto-retry on transient errors (but not on abort errors)
  useEffect(() => {
    if (error && retryCount < MAX_RETRIES && !error.includes('aborted') && !error.includes('canceled')) {
      const timeoutId = setTimeout(() => {
        console.log(`Auto-retrying leaderboard load (${retryCount + 1}/${MAX_RETRIES})`);
        loadLeaderboard(players.length === 0, searchQuery);
      }, 2000 * (retryCount + 1)); // Exponential backoff: 2s, 4s, 6s

      return () => clearTimeout(timeoutId);
    }
  }, [error, retryCount, players.length, searchQuery, loadLeaderboard]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return; // Don't observe if no more data

    // Cleanup existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting && 
          hasMore && 
          !loading && 
          !loadingMore && 
          !isLoadingRef.current
        ) {
          console.log('Intersection triggered, loading more data...');
          loadLeaderboard(false, searchQuery);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px', // Start loading 100px before the sentinel comes into view
      }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, searchQuery, loadLeaderboard]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAbortController();
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [cleanupAbortController]);

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      const query = searchInput.trim();
      setSearchQuery(query);
      setError(null); // Clear any existing errors
      isLoadingRef.current = false; // Reset loading ref for new search
      loadLeaderboard(true, query);
    }
  };

  const handleRefresh = () => {
    setError(null); // Clear any existing errors
    isLoadingRef.current = false; // Reset loading ref for refresh
    loadLeaderboard(true, searchQuery);
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    isLoadingRef.current = false;
    loadLeaderboard(players.length === 0, searchQuery);
  };

  // Initial load
  useEffect(() => {
    loadLeaderboard(true);
  }, []);

  // Calculate rank for each player
  const getPlayerRank = (player, index) => {
    // Use rank from API if available, otherwise compute based on position
    if (player.rank !== undefined && player.rank !== null) {
      return player.rank;
    }
    if (player.global_rank !== undefined && player.global_rank !== null) {
      return player.global_rank;
    }
    // Compute rank as index + 1 (since we maintain order from API)
    return index + 1;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white pb-20 font-prompt">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">อันดับ</h1>
          <Button 
            onClick={handleRefresh}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Search */}
        <Card className="p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-gray-400" />
            <Input
              placeholder="ค้นหาผู้เล่น..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearch}
              className="flex-1 border-none p-0 focus-visible:ring-0"
            />
            <Button 
              onClick={handleSearch}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              ค้นหา
            </Button>
          </div>
          {searchQuery && (
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                ผลการค้นหา "{searchQuery}": {total} รายการ
              </p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSearchInput('');
                  isLoadingRef.current = false;
                  loadLeaderboard(true, '');
                }}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                ล้างการค้นหา
              </Button>
            </div>
          )}
        </Card>

        {/* Leaderboard List */}
        {loading && players.length === 0 ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-16" />
                </div>
              </Card>
            ))}
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium mb-2">
              {searchQuery ? 'ไม่พบผู้เล่น' : 'ไม่มีข้อมูลอันดับ'}
            </p>
            <p className="text-gray-400">
              {searchQuery ? 'ลองค้นหาด้วยคำอื่น' : 'กลับมาดูใหม่ภายหลัง'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {players.map((player, index) => {
              const rank = getPlayerRank(player, index);
              return (
                <Card 
                  key={`${player.id || player.user_id}-${index}`} 
                  className={`p-4 ${getRankStyle(rank)}`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Rank */}
                    <div className="flex items-center justify-center w-8 h-8">
                      {getRankIcon(rank)}
                    </div>

                    {/* Player Info */}
                    <div className="flex items-center space-x-3 flex-1">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={player.avatar} alt={formatDisplayName(player.name)} />
                        <AvatarFallback>{getInitials(player.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate">
                            {formatDisplayName(player.name)}
                          </p>
                          {player.tier_label && (
                            <Badge variant="secondary" className="text-xs">
                              {player.tier_label}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          แมทซ์: {player.matches || 0}
                        </p>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">
                        {player.rating || '—'}
                      </p>
                      <p className="text-xs text-gray-500">Elo</p>
                    </div>
                  </div>
                </Card>
              );
            })}

            {/* Intersection Observer Sentinel */}
            {hasMore && (
              <div 
                ref={sentinelRef} 
                className="w-full h-10 flex items-center justify-center"
                role="status"
                aria-live="polite"
                aria-label={loadingMore ? "กำลังโหลดข้อมูลเพิ่มเติม" : "พร้อมโหลดข้อมูลเพิ่มเติม"}
              />
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
              <div 
                className="text-center py-4"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600">กำลังโหลดเพิ่มเติม...</p>
                </div>
              </div>
            )}

            {/* End of List */}
            {!hasMore && players.length > 0 && (
              <div className="text-center pt-4 pb-4">
                <div className="border-t border-gray-200 pt-4">
                  <Trophy className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 font-medium">
                    แสดงครบทั้งหมดแล้ว
                  </p>
                  <p className="text-xs text-gray-400">
                    {searchQuery ? `พบ ${players.length} รายการจากการค้นหา "${searchQuery}"` : `ทั้งหมด ${players.length} ผู้เล่น`}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Display with Retry */}
        {error && !error.includes('aborted') && !error.includes('canceled') && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm mb-2">{error}</p>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleRetry} 
                variant="outline" 
                size="sm"
                disabled={loading || loadingMore}
              >
                {loading || loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                    กำลังลองใหม่...
                  </>
                ) : (
                  'ลองใหม่'
                )}
              </Button>
              {retryCount > 0 && (
                <span className="text-xs text-gray-500">
                  ({retryCount}/{MAX_RETRIES} ครั้ง)
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <BottomTabBar />
    </div>
  );
};

export default LeaderboardPage;